import { useEffect, useRef } from "react";
import { type QueryResult } from "src/apolloHooks";
import { usePageVisibility } from "./usePageVisibility";

/**
 * Refetches `query` each time the user comes back to this tab, for pages that send the user elsewhere to act on
 * what they show (e.g. "go void this PO") and would otherwise keep showing the stale state when they return.
 */
export function useRefetchOnReturn<T>(query: QueryResult<T, any>) {
  const isVisible = usePageVisibility();
  // Seeded as visible so a page mounted in a background tab doesn't refetch right after its initial load
  const wasVisible = useRef(true);

  useEffect(() => {
    const returned = isVisible && !wasVisible.current;
    wasVisible.current = isVisible;
    // A failed refetch lands on `query.error` like any other, so the rejection has nothing left to report
    if (returned) query.refetch().catch(() => {});
    // Only re-run on visibility changes; `query` is a new object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);
}
